/**
 * DatePicker Component - Constants
 * ================================
 */

import { getIcon } from '../../icons';

export type DatePickerSize = 'sm' | 'md';

export interface DatePickerSizeStyles {
  trigger: string;
  text: string;
}

export interface DatePickerDefaultValues {
  size: DatePickerSize;
}

export const WEEKDAYS: string[] = ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa', 'Do'];

export const MONTHS: string[] = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

export const MONTHS_SHORT: string[] = [
  'ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'
];

// Figma especifica un solo tamaño de trigger (h-48, px-4/py-2, igual que Select);
// `sm`/`md` se conservan como API — solo varía el tamaño de texto.
export const SIZES: Record<DatePickerSize, DatePickerSizeStyles> = {
  sm: { trigger: 'ec-px-4 ec-py-2', text: 'ec-text-base' },
  md: { trigger: 'ec-px-4 ec-py-2', text: 'ec-text-base' }
};

export const BASE_CLASSES = {
  wrapper: 'ec-flex ec-flex-col ec-gap-1',
  relative: 'ec-relative',
  // Trigger (patrón field §1, igual que Select): h-48, borde gray/100 (#CCCCCC).
  trigger:
    'ec-w-full ec-h-12 ec-flex ec-items-center ec-justify-between ec-gap-3 ec-bg-color-surface ' +
    'ec-border ec-border-gray-light ec-rounded-lg ec-transition-colors ec-cursor-pointer ' +
    'focus-visible:ec-outline-none focus-visible:ec-border-color-primary focus-visible:ec-ring-1 focus-visible:ec-ring-color-primary',
  triggerDisabled: 'ec-bg-color-bg ec-cursor-not-allowed',
  // Error (patrón field §1): sin borde rojo — el texto del valor va en text-danger.
  // Colores del texto del trigger: mutuamente excluyentes (nunca combinar dos).
  triggerText: 'ec-flex ec-items-center ec-gap-2 ec-truncate',
  triggerTextColor: 'ec-text-color-text',
  triggerTextError: 'ec-text-color-text-danger',
  triggerPlaceholder: 'ec-text-color-text-muted',
  chevron: 'ec-w-4 ec-h-4 ec-text-color-text-muted ec-flex-shrink-0',
  // Panel/calendario: blanco, sin borde, radio 16, padding 12 (mined: p-[12px] rounded-[16px]).
  panel:
    'ec-absolute ec-z-20 ec-left-0 ec-mt-1 ec-w-80 ec-max-w-[calc(100vw-2rem)] ec-bg-color-surface ' +
    'ec-rounded-2xl ec-shadow-card ec-p-3',
  header: 'ec-flex ec-items-center ec-justify-between ec-mb-3',
  navBtn:
    'ec-inline-flex ec-items-center ec-justify-center ec-w-6 ec-h-6 ec-rounded-full ec-bg-transparent ec-border-0 ' +
    'ec-text-color-text hover:ec-bg-color-bg ec-cursor-pointer ' +
    'focus-visible:ec-outline-none focus-visible:ec-ring-2 focus-visible:ec-ring-color-primary',
  navIcon: 'ec-w-6 ec-h-6',
  // Selector mes/año: mes + flecha (reusa el ícono "next") + año, mined "Month + Year selector".
  monthYear: 'ec-flex ec-items-center ec-gap-1',
  monthLabel: 'ec-text-base ec-text-color-text ec-font-semibold',
  weekRow: 'ec-grid ec-grid-cols-7 ec-mb-1',
  // Weekday: BLUE (text/link #1744D4 == --ec-color-primary), semibold, 16px (mined).
  weekday: 'ec-text-center ec-text-base ec-font-semibold ec-text-color-primary ec-py-1',
  grid: 'ec-grid ec-grid-cols-7',
  dayEmpty: 'ec-h-10',
  day: 'ec-flex ec-items-center ec-justify-center ec-h-10 ec-w-10 ec-mx-auto ec-text-base ec-text-color-text ec-cursor-pointer ec-transition-colors',
  dayDefault: 'ec-rounded-full hover:ec-bg-color-bg',
  // Seleccionado: cuadrado outline azul (border-2 primary/600, rounded-16, SIN fill) — no círculo relleno (mined).
  daySelected: 'ec-rounded-2xl ec-border-2 ec-border-color-primary',
  label: 'ec-text-base ec-text-color-text-secondary',
  help: 'ec-text-caption ec-font-medium',
  // Colores del help: mutuamente excluyentes.
  helpColor: 'ec-text-color-text-secondary',
  helpError: 'ec-text-color-text-danger'
};

/**
 * Iconos SVG predefinidos
 *
 * El trigger usa el mismo affordance que Select (chevron/caret-down), NO un
 * ícono de calendario — el frame Figma del trigger ("Input select") es el
 * mismo componente que usa Select/Día/Mes/Año.
 * Las keys públicas (icon="prev"/"next") se mantienen igual — cero breaking.
 * Los valores vienen del registro central (`src/icons`) vía los alias
 * semánticos `prev→caret-left` y `next→caret-right`.
 */
export const ICONS = {
  chevron: getIcon('caret-down') || '',
  prev: getIcon('prev') || '',
  next: getIcon('next') || ''
};

export const DEFAULTS: DatePickerDefaultValues = { size: 'md' };
